import React, { useCallback, useMemo } from 'react';
import { MdFilterList, MdStars } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { selectorFiltersIsVisible, TOGGLE_GERENCIADOR_FILTERS_IS_VISIBLE } from '../../../../../reducers';
import { ADJUSTED_PRICE_BY_LIMIT, LimitFilterValue } from '../../../../../reducers/gerenciadorPrecos/quickActionsFilter';
import useGerenciadorSort from '../../hooks/useGerenciadorSort';
import { BaseQuickActionFilterProps, useQuickActionFilter } from '../QuickActionFilter';

export const BROKEN_LIMIT_FILTER_NOTE =
  'Este filtro olha para o preço sugerido pelo cálculo automático, não para as edições, e considera todas as visões. ' +
  'Por exemplo: um item sem a sinalização na linha pode estar quebrando em outra visão.';

const LIMIT_FILTER_SKINS: Record<`${LimitFilterValue}`, BaseQuickActionFilterProps['skin']> = {
  true: 'error',
  false: 'primary',
  [ADJUSTED_PRICE_BY_LIMIT]: 'warning',
};

const useGerenciadorQuickActionFilters = () => {
  const dispatch = useDispatch();
  const isVisible = useSelector(selectorFiltersIsVisible);

  const { onSortColumn, initialState: initialSort } = useGerenciadorSort();

  const { state, handleToggleChangedPrice, handleSelectFilterLimits, handleSelectFilterLimitsClick, handleToggleQuickActionFilter } = useQuickActionFilter();

  const changedPricesSkin = useMemo(() => {
    let skin: BaseQuickActionFilterProps['skin'] = 'neutral';
    if (state.changedPrices.isActive) {
      skin = 'primary';
    }
    return skin;
  }, [state.changedPrices.isActive]);

  const changedPrices = useMemo(
    () => ({
      placeholder: 'Somente preços alterados',
      type: 'button',
      icon: 'MdPriceChange',
      skin: changedPricesSkin,
      onClick: handleToggleChangedPrice,
    }),
    [handleToggleChangedPrice, changedPricesSkin],
  );

  const isFilterVisibleSkin = useMemo<BaseQuickActionFilterProps['skin']>(() => {
    return isVisible ? 'primary' : 'neutral';
  }, [isVisible]);

  const isFilterVisible = useMemo(
    () => ({
      placeholder: 'Alternar visualização dos filtros',
      type: 'button',
      icon: <MdFilterList />,
      onClick: () => dispatch(TOGGLE_GERENCIADOR_FILTERS_IS_VISIBLE()),
      skin: isFilterVisibleSkin,
    }),
    [dispatch, isFilterVisibleSkin],
  );

  const selectLimitsSkin = useMemo<BaseQuickActionFilterProps['skin']>(() => {
    if (!state.brokenLimit.isActive) return 'neutral';
    return LIMIT_FILTER_SKINS[`${state.brokenLimit.value}`];
  }, [state.brokenLimit]);

  const selectLimits = useMemo(
    () => ({
      placeholder: 'Filtrar limites',
      type: 'select',
      icon: 'MdReport',
      skin: selectLimitsSkin,
      options: [
        {
          id: 'BROKEN_LIMITS',
          value: true,
          label: 'Sugestão com limites quebrados',
        },
        {
          id: 'UNBROKEN_LIMITS',
          value: false,
          label: 'Sugestão sem limites quebrados',
        },
        {
          id: ADJUSTED_PRICE_BY_LIMIT,
          value: ADJUSTED_PRICE_BY_LIMIT,
          label: 'Preços ajustados pelos limites',
        },
      ],
      note: BROKEN_LIMIT_FILTER_NOTE,
      onSelect: handleSelectFilterLimits,
      onClick: handleSelectFilterLimitsClick,
    }),
    [handleSelectFilterLimits, handleSelectFilterLimitsClick, selectLimitsSkin],
  );

  const derrivedProductsSkin = useMemo(() => {
    const skin: BaseQuickActionFilterProps['skin'] = 'neutral';
    if (state.isProductMaster.isActive) {
      return 'primary';
    }
    return skin;
  }, [state.isProductMaster.isActive]);

  const onClickDerrivedProducts = useCallback(() => {
    if (state.isProductMaster.isActive) {
      onSortColumn(initialSort.type, initialSort.orderBy);
    } else {
      onSortColumn('productFamilyMaster', 'desc');
    }
    handleToggleQuickActionFilter('isProductMaster');
  }, [handleToggleQuickActionFilter, onSortColumn, initialSort, state.isProductMaster.isActive]);

  const derrivedProducts = useMemo(
    () => ({
      placeholder: 'Filtrar produtos derivados',
      type: 'button',
      icon: <MdStars />,
      onClick: onClickDerrivedProducts,
      skin: derrivedProductsSkin,
    }),
    [derrivedProductsSkin, onClickDerrivedProducts],
  );

  const filters = useMemo(
    () => ({
      changedPrices,
      selectLimits,
      derrivedProducts,
      isFilterVisible,
    }),
    [changedPrices, selectLimits, derrivedProducts, isFilterVisible],
  );

  return filters;
};

export default useGerenciadorQuickActionFilters;
